import { differenceInBusinessDays } from 'date-fns/differenceInBusinessDays';
import { ArticleType } from '@appTypes/articleType';
import { DocumentTypeLabel } from '@appTypes/testType';
import { SiteStructureType } from '@appTypes/siteStructureType';
import { AuthorInfo } from '@components/structured-data/SchemaArticle/SchemaArticle.types';
import type { ReduxError } from '@universal/errors';

export const openGraphImageWidth = 1200;

interface DataState<T> {
  data?: T;
  meta: {
    isUpdating: boolean;
    isEnriched: boolean;
    fetchedAt?: string;
    isLoggedIn?: boolean;
  };
  error?: ReduxError;
}

interface MappedCategories {
  [key: string]: DataState<SiteStructureType>;
}

/**
 * Computes category-related data for an article
 */
export const getArticleCategoryData = (article: ArticleType, mappedCategories: MappedCategories) => {
  const { category, parent, name } = article;
  const mappedCategory = category?.slug ? mappedCategories[category.slug]?.data : null;
  const isHiddenCategory = mappedCategory?.isHiddenCategory;

  // If article has no category, check for parent article
  const mappedParentCategory =
    !mappedCategory && parent && parent.category?.slug ? mappedCategories[parent.category.slug].data : null;

  const pageCategory = mappedCategory
    ? `${mappedCategory?.prettyUrl}/${name}`
    : `${mappedParentCategory?.prettyUrl}/${parent?.slug}/${name}`;

  return {
    mappedCategory,
    mappedParentCategory,
    pageCategory,
    isHiddenCategory,
  };
};

/**
 * SEO-related data for article.
 */
export const getArticleSEOData = (article: ArticleType) => {
  const { seoConfig, ingress, name } = article;
  const seoDescription = seoConfig?.introduction ? seoConfig.introduction : ingress;
  const seoTitle = seoConfig?.title ? seoConfig.title : name;

  return {
    seoDescription,
    seoTitle,
  };
};

/**
 * Computes structured data.
 */
export const getArticleSchemaData = (
  article: ArticleType,
  mappedCategory: SiteStructureType | undefined,
  mappedParentCategory: SiteStructureType | undefined,
  articleUrl: string,
): {
  schemaType: 'Article' | 'NewsArticle';
  schemaArticleSection: string | undefined;
  schemaAuthor: string | AuthorInfo;
  schemaUrl: string;
} => {
  const { type, meta, slug } = article;

  const schemaType = type === DocumentTypeLabel.nafnoNewsArticle ? 'NewsArticle' : 'Article';
  const schemaArticleSection =
    mappedCategory?.seoConfig?.title ||
    mappedCategory?.name ||
    mappedParentCategory?.seoConfig?.title ||
    mappedParentCategory?.name;

  // Build author object with bio and image if available
  const schemaAuthor: string | AuthorInfo = meta?.editor?.name
    ? {
        name: meta.editor.name,
        ...(meta.editor.bio && { bio: meta.editor.bio }),
        ...(meta.editor.imageUrl && { imageUrl: meta.editor.imageUrl }),
        ...(meta.editor.slug && { slug: meta.editor.slug }),
      }
    : 'NAF';

  const schemaUrl = articleUrl || `/${slug}`;

  return {
    schemaType,
    schemaArticleSection,
    schemaAuthor,
    schemaUrl,
  };
};

/**
 * Author bio title, e.g. "Ola Nordmann, journalist". Position is lower-cased only when paired with a name.
 */
export const getAuthorBioTitle = (name?: string, position?: string) => {
  if (!position) return name;
  if (!name) return position;

  const lowerCasedPosition = position.charAt(0).toLowerCase() + position.slice(1);
  return `${name}, ${lowerCasedPosition}`;
};

/**
 * GTM args for article page.
 */
export const getArticleGTMArgs = (article: ArticleType, pageCategory: string) => {
  const { id, meta, name, userAccessibility } = article;

  return {
    page_type: 'ArticlePage',
    pageCategory,
    contentId: id,
    author: meta?.editor?.name,
    headline: name,
    publishDate: meta?.publishAt,
    daysSincePublish: differenceInBusinessDays(new Date(), new Date(meta?.publishAt)),
    isPaywall: userAccessibility.viewType !== 'Free',
    viewType: userAccessibility.viewType,
  };
};
